import { createContext, use, useMemo, useRef } from "react";
import type { ReactNode } from "react";
import { CollisionPriority } from "@dnd-kit/abstract";
import { pointerIntersection } from "@dnd-kit/collision";
import type {
  FigureLayoutResponse,
  FigureResponse,
} from "@patent-studio/api-types";
import {
  SortableLanes,
  useLane,
  useSortableRow,
} from "@/components/ui/SortableLanes";
import { isPending } from "@/hooks/use-figures";
import { ROOT, laneOf, useFigureLayout } from "@/hooks/use-figure-layout";
import type { Lanes } from "@/hooks/use-figure-layout";
import type { FigureGroup } from "@/lib/figure-labels";
import { aimedAtByTravelWhenAGroup } from "./figure-collision";
import { namedGroup } from "@/lib/figure-group-naming";
import { named } from "@/lib/figure-naming";

/** Everything a drag can be about, so an announcement can name it. */
interface Naming {
  figures: FigureResponse[];
  groups: FigureGroup[];
}

/**
 * How a reader hears each thing a figure drag can be about.
 *
 * The figures are read when something is announced rather than when this is
 * built, because the plugin holding it is constructed once and the figures
 * arrive later, with the query.
 */
export function figureName(namesNow: () => Naming) {
  return (id: unknown) => {
    const { figures, groups } = namesNow();
    const figure = figures.find((f) => f.file_id === id);
    if (figure !== undefined) return named(figure);
    const group = groups.find(
      (g) => g.group_id === id || laneOf(g.group_id) === id,
    );
    if (group === undefined) return "a figure";
    // A drop onto the lane goes inside the group; a drop onto its row goes
    // beside it. Same group, two different outcomes, so they are said
    // differently.
    return laneOf(group.group_id) === id
      ? `inside ${namedGroup(group)}`
      : namedGroup(group);
  };
}

type Entries = NonNullable<FigureLayoutResponse["entries"]>;

/**
 * The figures the arrangement has no position for.
 *
 * One still belongs to the task, and the arranging screens are the only place
 * it can be given a position, so it follows the arrangement and moves like
 * anything in it. A figure the server has not confirmed is not among them: it
 * has no identifier any write could name yet.
 */
function unplaced(
  entries: Entries,
  figures: readonly FigureResponse[],
): FigureResponse[] {
  const placed = new Set(
    entries.flatMap((entry) =>
      entry.kind === "group"
        ? (entry.members ?? []).map((member) => member.file_id)
        : [entry.file_id],
    ),
  );
  return figures.filter(
    (figure) => !placed.has(figure.file_id) && !isPending(figure),
  );
}

/**
 * The arrangement as one list per lane, which is what a drag is resolved
 * against.
 *
 * The top-level lane holds groups alongside figures, so a group moves as a unit
 * and a figure can be dropped either side of one. A group's members are a lane
 * of their own, addressed by a name that cannot collide with the group's row.
 */
function lanesFor(entries: Entries, loose: FigureResponse[]): Lanes {
  return {
    [ROOT]: [
      ...entries.map((entry) =>
        entry.kind === "group" ? entry.group_id : entry.file_id,
      ),
      ...loose.map((figure) => figure.file_id),
    ],
    ...Object.fromEntries(
      entries
        .filter((entry) => entry.kind === "group")
        .map((group) => [
          laneOf(group.group_id),
          (group.members ?? []).map((member) => member.file_id),
        ]),
    ),
  };
}

/** What a screen inside the drag is given to draw from and to act through. */
export interface ArrangementView {
  /** The arrangement as the screen is showing it, which during a drag runs ahead of the server's. */
  lanes: Lanes;
  /** What a release right now would land on, anywhere on the screen. */
  over: string | null;
  groups: FigureGroup[];
  byId: Map<string, FigureResponse>;
  /** The layout's write seam, one instance, so every change queues behind the last. */
  acts: ReturnType<typeof useFigureLayout>;
}

/** The arrangement a row inside `ArrangementDrag` reads, for the acts it offers besides the drag. */
const ArrangementContext = createContext<ArrangementView | null>(null);

/** The arrangement this row sits in, or null outside an `ArrangementDrag`. */
export function useArrangement(): ArrangementView | null {
  return use(ArrangementContext);
}

function without(lanes: Lanes, fileId: string): Lanes {
  return Object.fromEntries(
    Object.entries(lanes).map(([lane, ids]) => [
      lane,
      ids.filter((id) => id !== fileId),
    ]),
  );
}

/** File names as a person counts them: fig2 before fig10. */
const NATURAL = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

/**
 * The arrangement with one row sent to a place in its own list, counted from 1 and held to the
 * list's ends. A group is one place at the top level, and a grouped figure moves among its
 * group's members.
 */
export function movedToPosition(
  lanes: Lanes,
  id: string,
  position: number,
): Lanes {
  const lane = Object.keys(lanes).find((key) => lanes[key].includes(id));
  if (lane === undefined) return lanes;
  const rows = lanes[lane].filter((row) => row !== id);
  const at = Math.min(Math.max(Math.round(position), 1), rows.length + 1);
  rows.splice(at - 1, 0, id);
  return { ...lanes, [lane]: rows };
}

/**
 * The arrangement sorted by file name in natural order. Each group's members are sorted within
 * it, and the group moves as one, placed by its first member's name; an empty group goes last.
 */
export function sortedByFileName(
  lanes: Lanes,
  byId: Map<string, FigureResponse>,
): Lanes {
  const name = (id: string) => byId.get(id)?.original_filename ?? "";
  const sorted: Lanes = Object.fromEntries(
    Object.entries(lanes).map(([lane, ids]) => [
      lane,
      lane === ROOT
        ? ids
        : [...ids].sort((a, b) => NATURAL.compare(name(a), name(b))),
    ]),
  );
  const key = (id: string) => {
    if (byId.has(id)) return name(id);
    const members = laneOf(id) in sorted ? sorted[laneOf(id)] : [];
    const first = members.at(0);
    return first === undefined ? "\uffff" : name(first);
  };
  sorted[ROOT] = [...(sorted[ROOT] ?? [])].sort((a, b) =>
    NATURAL.compare(key(a), key(b)),
  );
  return sorted;
}

/** The arrangement with a figure moved to the end of a group, wherever it sat. */
export function movedIntoGroup(
  lanes: Lanes,
  fileId: string,
  groupId: string,
): Lanes {
  const rest = without(lanes, fileId);
  const lane = laneOf(groupId);
  return { ...rest, [lane]: [...(rest[lane] ?? []), fileId] };
}

/** The arrangement with a grouped figure moved out, to just after its group at the top level. */
export function movedOutOfGroup(
  lanes: Lanes,
  fileId: string,
  groupId: string,
): Lanes {
  const rest = without(lanes, fileId);
  const top = [...(rest[ROOT] ?? [])];
  top.splice(top.indexOf(groupId) + 1, 0, fileId);
  return { ...rest, [ROOT]: top };
}

/**
 * The drag that rearranges a task's figures, shared by every screen that offers it.
 *
 * Owned once rather than per screen, because the Organise and Define screens
 * write the same layout through the same versioned replacement, and two
 * readings of how a drag becomes a write would let them disagree about what a
 * reorder means. How a drag follows the pointer and becomes one write is
 * `SortableLanes`'; what is left here is what figures and groups are called and
 * which lanes they sit in.
 */
export function ArrangementDrag({
  taskId,
  figures,
  layout,
  children,
}: {
  taskId: string | undefined;
  figures: readonly FigureResponse[];
  layout: FigureLayoutResponse;
  children: (view: ArrangementView) => ReactNode;
}) {
  const acts = useFigureLayout(taskId);

  // Once per server answer, not once per pointer move: nothing in these can
  // change during a drag.
  const { groups, byId, served } = useMemo(() => {
    const entries = layout.entries ?? [];
    return {
      groups: entries.filter((entry) => entry.kind === "group"),
      byId: new Map(figures.map((figure) => [figure.file_id, figure])),
      served: lanesFor(entries, unplaced(entries, figures)),
    };
  }, [layout, figures]);

  const namesRef = useRef({ figures: [...figures], groups });
  namesRef.current = { figures: [...figures], groups };

  return (
    <SortableLanes
      served={served}
      name={figureName(() => namesRef.current)}
      onArrange={acts.arrange}
    >
      {({ lanes, over }) => {
        const view = { lanes, over, groups, byId, acts };
        return (
          <ArrangementContext value={view}>{children(view)}</ArrangementContext>
        );
      }}
    </SortableLanes>
  );
}

/**
 * The drag roles, owned here so both arranging screens draw their own markup over one
 * policy. Tuning a collision rule on the Organise screen, which is the screen anyone
 * tests on, must reach the navigator without anyone remembering a second file.
 */

/** A figure as a sortable row in the lane it currently sits in. */
export function useFigureSortable(fileId: string, index: number, lane: string) {
  return useSortableRow({
    id: fileId,
    index,
    lane,
    type: "figure",
    // What a row accepts depends on where it sits. Inside a group it takes figures
    // only, because a group cannot hold a group and the refusal belongs at the drop
    // rather than after the arrangement has been shown. At the top level it also
    // takes a group, since a group landing on a figure there is a reorder and
    // nothing deeper.
    accept: lane === ROOT ? ["figure", "group"] : "figure",
    // Only the top level is ever asked to take a group, so only it needs the rule
    // that keeps a tall one from being resolved against its own bulk.
    collisionDetector: lane === ROOT ? aimedAtByTravelWhenAGroup : undefined,
  });
}

/**
 * A group's two roles: a sortable row at the top level, so it moves as a unit, and a
 * lane of its own that takes figures. The two are different elements: the row's own
 * drop target is its header, because a target covering the whole lane would swallow
 * every drop meant for the members inside it.
 */
export function useGroupDrag(groupId: string, index: number) {
  const row = useSortableRow({
    id: groupId,
    index,
    lane: ROOT,
    type: "group",
    // This is the row, not the inside, so it takes whatever may sit beside it.
    // Refusing a group here would cost the ability to put one group before
    // another. Going inside is refused by the lane, which takes figures only.
    accept: ["figure", "group"],
    collisionDetector: aimedAtByTravelWhenAGroup,
  });
  const lane = useLane({
    id: laneOf(groupId),
    accept: "figure",
    // The lane sits inside the group's own row, so without this the row wins
    // every collision and nothing can ever be dropped into the group.
    collisionPriority: CollisionPriority.High,
    // Winning on priority is only right where the lane genuinely is. The default
    // rule falls back to overlapping area once the pointer is outside a target,
    // and a figure card is wide enough to overlap this lane while being aimed at
    // the gap above the group, which the priority then turns into a drop inside
    // it. Asking for the pointer confines the lane to its own area and gives the
    // row above the band that belongs to it.
    collisionDetector: pointerIntersection,
  });
  return { ...row, laneRef: lane.ref };
}

/**
 * The top level as somewhere a figure can be dropped rather than only a list.
 *
 * A group is a lane inside a row, so with nothing beside it every part of the list
 * belongs to the group and a figure being taken out has nowhere to land. The top level
 * accepts drops itself, above or below whatever it holds. It yields to the rows and
 * lanes inside it, which is what makes it the target only when nothing else is under
 * the pointer, and what lets a figure be dragged back out of a group.
 */
export function useTopLevelDrop() {
  return useLane({
    id: ROOT,
    accept: "figure",
    collisionPriority: CollisionPriority.Low,
  });
}

/** One top-level thing to draw: a group with the members its lane holds, or a figure. */
export type TopLevelEntry =
  | {
      kind: "group";
      id: string;
      at: number;
      group: FigureGroup;
      members: string[];
    }
  | { kind: "figure"; id: string; at: number; figure: FigureResponse };

/**
 * The top level as the drag is showing it, one entry per thing to draw.
 *
 * The position indexes the lane rather than the drawn list, because a figure whose
 * image has not arrived is not drawn but still holds its place in the arrangement. A
 * repeated id is drawn once: the library can report one mid-move.
 */
export function topLevelEntries(view: ArrangementView): TopLevelEntry[] {
  const { lanes, groups, byId } = view;
  const groupById = new Map(groups.map((group) => [group.group_id, group]));
  const seen = new Set<string>();
  return (lanes[ROOT] ?? []).flatMap((id, at): TopLevelEntry[] => {
    if (seen.has(id)) return [];
    seen.add(id);
    const group = groupById.get(id);
    if (group !== undefined) {
      return [
        { kind: "group", id, at, group, members: lanes[laneOf(id)] ?? [] },
      ];
    }
    const figure = byId.get(id);
    return figure === undefined ? [] : [{ kind: "figure", id, at, figure }];
  });
}
